// components/DegreeAudit/Common/ConstraintViolations.tsx
// UI Phase 0.2: violations render as human-readable sentences (never raw JSON).
// The backend schema (`ConstraintViolations` in routes/schemas/degree_audit.py)
// sends free-form List[Dict[str, Any]] per category, so formatting is
// shape-tolerant with a readable key/value fallback.

import { memo } from 'react';
import { AlertCircle } from 'lucide-react';

export interface ConstraintViolationsData {
  prerequisites?: unknown[];
  corequisites?: unknown[];
  mutual_exclusions?: unknown[];
  [key: string]: unknown[] | undefined;
}

export interface ConstraintViolationsProps {
  violations: ConstraintViolationsData | null | undefined;
}

const asCodes = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value;
  if (Array.isArray(value)) {
    const codes = value.filter((x): x is string => typeof x === 'string' && !!x.trim());
    if (codes.length === 0) return null;
    if (codes.length === 1) return codes[0];
    return `${codes.slice(0, -1).join(', ')} or ${codes[codes.length - 1]}`;
  }
  return null;
};

const humanizeSemester = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.replace(/_/g, ' ') : null;

export const formatViolation = (violation: unknown, kind: string): string => {
  if (typeof violation === 'string') return violation;

  if (violation && typeof violation === 'object') {
    const v = violation as Record<string, unknown>;
    if (typeof v.message === 'string' && v.message.trim()) return v.message;

    const course = asCodes(v.course) ?? asCodes(v.course_code);
    const other =
      asCodes(v.missing) ??
      asCodes(v.prerequisite) ??
      asCodes(v.required) ??
      asCodes(v.requires) ??
      asCodes(v.corequisite) ??
      asCodes(v.conflicts_with);
    const semester = humanizeSemester(v.semester);

    if (course && other) {
      const relation =
        kind === 'mutual_exclusions'
          ? `${course} can't be taken together with ${other}`
          : kind === 'corequisites'
            ? `${course} must be taken alongside ${other}`
            : `${course} requires ${other} first`;
      return semester ? `${relation} (${course} is planned in ${semester})` : relation;
    }

    // Last resort: readable key/value pairs — never raw JSON.
    const parts = Object.entries(v)
      .filter(([, val]) => typeof val === 'string' || typeof val === 'number')
      .map(([key, val]) => `${key.replace(/_/g, ' ')}: ${val}`);
    if (parts.length > 0) return parts.join(' · ');
  }

  return 'Constraint violation (details unavailable)';
};

const SECTIONS: Array<{ key: keyof ConstraintViolationsData & string; title: string }> = [
  { key: 'prerequisites', title: 'Missing Prerequisites:' },
  { key: 'corequisites', title: 'Missing Corequisites:' },
  { key: 'mutual_exclusions', title: 'Courses That Conflict:' },
];

const ConstraintViolations = memo(({ violations }: ConstraintViolationsProps) => {
  // Guard on the renderable SECTIONS, not every key: a category this component
  // doesn't know how to render must not produce a header-only red panel.
  if (
    !violations ||
    SECTIONS.every(({ key }) => !violations[key] || violations[key]!.length === 0)
  ) {
    return null;
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 md:p-4">
      <h3 className="font-medium text-red-900 mb-2 md:mb-3 flex items-center text-sm md:text-base">
        <AlertCircle className="w-4 md:w-5 h-4 md:h-5 mr-1 md:mr-2 print-hide" />
        Constraint Violations
      </h3>

      {SECTIONS.map(({ key, title }) => {
        const items = violations[key];
        if (!items || items.length === 0) return null;
        return (
          <div key={key} className="mb-3 md:mb-4 last:mb-0">
            <h4 className="font-medium text-red-800 mb-1 md:mb-2 text-sm md:text-base">
              {title}
            </h4>
            <ul className="space-y-1 text-xs md:text-sm text-red-800">
              {items.map((violation, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="mr-1 md:mr-2">•</span>
                  <span>{formatViolation(violation, key)}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
});

ConstraintViolations.displayName = 'ConstraintViolations';

export default ConstraintViolations;
